import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, CheckCircle2 } from "lucide-react";
import api, { imageSrc } from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Retailer/Wholesaler order placing — direct numeric keypad input.
// No +/- stepper. Tap the box, type boxes count.
export default function PlaceOrder() {
  const nav = useNavigate();
  const loc = useLocation();
  const { t, displayName } = useLang();
  const editOrder = loc.state?.editOrder;
  const isEdit = !!editOrder;
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // {productId: "<numeric string>"}
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data));
    if (loc.state?.repeatItems) {
      const c = {};
      loc.state.repeatItems.forEach(it => { c[it.product_id] = String(it.quantity); });
      setCart(c);
    } else if (editOrder?.items) {
      const c = {};
      editOrder.items.forEach(it => { c[it.product_id] = String(it.quantity); });
      setCart(c);
    }
  // eslint-disable-next-line
  }, []);

  // Accept digits only. Clamp to integer >= 0. Empty string is allowed (means 0 / unset).
  const setQty = (id, raw) => {
    const cleaned = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    setCart(prev => {
      const next = { ...prev };
      if (!cleaned) delete next[id]; else next[id] = cleaned;
      return next;
    });
  };

  const cartItems = products
    .map(p => ({ product: p, qty: parseInt(cart[p.id] || "0", 10) || 0 }))
    .filter(x => x.qty > 0)
    .map(x => ({ product_id: x.product.id, name: x.product.name, quantity: x.qty }));

  const totalBoxes = cartItems.reduce((s, i) => s + i.quantity, 0);

  const submit = async () => {
    if (!cartItems.length) return;
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/orders/${editOrder.id}`, { items: cartItems });
        toast.success(t("order_modified"));
        nav("/shop/orders");
      } else {
        await api.post("/orders", { items: cartItems });
        setDone(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] grid place-items-center p-6">
        <div className="bg-white rounded-3xl p-8 sm:p-10 soft-shadow text-center max-w-md w-full fade-up" data-testid="order-sent-card">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-50 grid place-items-center mb-4">
            <CheckCircle2 className="w-12 h-12 text-[#34C759]" />
          </div>
          <div className="heading-font text-3xl font-bold text-[#1D1D1F] mb-2" data-testid="order-sent-title">{t("order_sent")}</div>
          <div className="text-[#86868B] mb-6">{totalBoxes} {t("boxes")} • {cartItems.length} {t("items")}</div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => nav("/shop/orders")}
              data-testid="go-to-orders-btn"
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-2xl py-4 text-lg font-bold"
            >
              {t("my_orders")}
            </button>
            <button
              onClick={() => nav("/shop")}
              data-testid="back-home-btn"
              className="w-full bg-white border border-gray-200 text-[#1D1D1F] rounded-2xl py-3 font-bold"
            >
              {t("back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-32">
      <TopBar title={isEdit ? t("update_order") : t("place_order")} onBack={() => nav(isEdit ? "/shop/orders" : "/shop")} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6 fade-up">
        {isEdit && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4 text-sm text-blue-800" data-testid="edit-mode-banner">
            <strong>{t("modify_order")}</strong> — {t("update_order_subtitle")}
          </div>
        )}
        <div className="space-y-3">
          {products.map(p => {
            const qtyStr = cart[p.id] || "";
            return (
              <div key={p.id} className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-4" data-testid={`product-${p.sku}`}>
                <img
                  src={imageSrc(p.image_url) || "https://images.unsplash.com/photo-1642429948905-62fc9c9b3f76?w=200"}
                  alt=""
                  className="w-16 h-16 rounded-xl object-cover bg-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#1D1D1F] heading-font truncate">{displayName(p)}</div>
                  <div className="text-xs text-[#86868B] uppercase tracking-wider mt-0.5">{t("boxes")}</div>
                </div>
                <div className="shrink-0">
                  <input
                    data-testid={`qty-input-${p.sku}`}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="0"
                    value={qtyStr}
                    onChange={(e) => setQty(p.id, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F", caretColor: "#0055FF" }}
                    className="w-24 h-14 text-center text-2xl font-bold rounded-2xl bg-[#F5F5F7] border border-transparent focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] outline-none transition placeholder:text-[#9CA3AF]"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 glass border-t border-white/40 p-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("quantity")}</div>
              <div className="heading-font text-2xl font-bold text-[#1D1D1F]" data-testid="cart-units">
                {totalBoxes} <span className="text-sm font-medium text-[#86868B]">{t("boxes")} • {cartItems.length} {t("items")}</span>
              </div>
            </div>
            <button
              data-testid="submit-order-btn"
              onClick={submit}
              disabled={submitting}
              className="bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl px-6 py-3 font-bold shadow-md flex items-center gap-2"
            >
              <Check className="w-5 h-5" /> {isEdit ? t("update_order") : t("submit_order")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
